// ApplicationsExport — "Download applications" on the Dashboard.
// Per role: how many applied and how they scored, as one Excel workbook with
// two sheets: "By role" (every application) and "All applications" (all of
// them, or only CV scores of 60% and above).
// Data from GET /api/dashboard/applications-export — the signed-in school only;
// Email and Phone are in the list only for recruitment administrators, decided
// by the server. Every download is logged. Shown only to people the server says
// may download (exportAccess on GET /api/dashboard).

import React, { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import api from '../../services/api';

const CHOICES = [
  { key: 'all', label: 'Download all applications', params: {} },
  { key: '60', label: 'Download 60% and above', params: { minScore: 60 } },
];

const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function nameFrom(res, fallback) {
  const cd = res.headers?.['content-disposition'] || '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  return m ? m[1] : fallback;
}

export default function ApplicationsExport({ access }) {
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  if (!access?.allowed) return null;

  const download = (choice) => {
    setBusy(choice.key);
    setMsg('');
    api.get('/dashboard/applications-export', { params: choice.params, responseType: 'blob' })
      .then((res) => {
        const blob = new Blob([res.data], { type: XLSX });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = nameFrom(res, `applications-${choice.key}-CONFIDENTIAL.xlsx`);
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setMsg('Could not prepare the download. Try again, or tell your developer.'))
      .finally(() => setBusy(''));
  };

  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow p-4">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <span>
          <span className="font-heading font-bold text-[15px]">Download applications</span>
          <span className="block text-xs text-gray-500">For each role, how many applied and how their CVs scored. One Excel file with two sheets: By role, and All applications.</span>
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mt-3">
        {CHOICES.map((c) => (
          <button key={c.key} onClick={() => download(c)} disabled={Boolean(busy)}
            className="px-3 py-1.5 rounded-dps text-sm border border-gray-300 text-gray-700 hover:border-orange-500 hover:text-orange-600 transition flex items-center gap-1.5 disabled:opacity-60">
            {busy === c.key ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} {c.label}
          </button>
        ))}
      </div>
      {msg && <div className="text-sm text-red-600 mt-2">{msg}</div>}
      <p className="text-[11px] text-gray-500 mt-2">
        {access.contactDetails
          ? 'Contains candidates’ contact details. Do not forward outside the school.'
          : 'Contact details are available to admins only.'}
      </p>
    </section>
  );
}
